import React, { useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/react';
import { UserRole } from '@repo/shared-types';
import { setClerkGetToken, setClerkSignOut } from '../../services/api/clerkAuth';
import { authApi } from '../../services/api/auth';
import { clearSessionAndRedirectToLogin } from '../../services/api/onAuthFailure';
import { useAppStore } from '../../store';
import { SignOutContext } from '../../contexts/SignOutContext';
import { logger } from '@repo/shared-utils';

const isUiTest = import.meta.env.MODE === 'test';

/**
 * Inside ClerkProvider. Wires getToken/signOut, Clerk sign-in state, backend profile → Zustand, and auth pending gate.
 */
export const ClerkAuthBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOutFn } = useAuth();
  const setClerkSignedIn = useAppStore((s) => s.setClerkSignedIn);
  const setAuthUser = useAppStore((s) => s.setAuthUser);
  const setAuthPending = useAppStore((s) => s.setAuthPending);
  const setCurrentUserId = useAppStore((s) => s.setCurrentUserId);

  useEffect(() => {
    if (isUiTest) {
      setAuthPending(false);
      return;
    }
    if (!isLoaded) {
      setAuthPending(true);
      return;
    }
    if (!isSignedIn) {
      setAuthPending(false);
      return;
    }
    setAuthPending(true);
  }, [isLoaded, isSignedIn, setAuthPending]);

  useEffect(() => {
    if (isUiTest) return;
    setClerkSignedIn(isSignedIn ?? false);
  }, [isSignedIn, setClerkSignedIn]);

  useEffect(() => {
    if (isUiTest) return;
    if (isLoaded && !isSignedIn) {
      setCurrentUserId('');
    }
  }, [isLoaded, isSignedIn, setCurrentUserId]);

  useEffect(() => {
    if (isUiTest) {
      setClerkGetToken(null);
      return;
    }
    setClerkGetToken(getToken ?? (() => Promise.resolve(null)));
    return () => setClerkGetToken(null);
  }, [getToken]);

  useEffect(() => {
    if (isUiTest) {
      setClerkSignOut(null);
      return;
    }
    setClerkSignOut(clerkSignOutFn ?? (() => Promise.resolve()));
    return () => setClerkSignOut(null);
  }, [clerkSignOutFn]);

  useEffect(() => {
    if (isUiTest) return;
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    void (async () => {
      try {
        // Use probe that does not trigger global `/auth/current-user` 401 side effects (no
        // `redirectToCompleteOnboarding` / hard navigation loop when profile is missing).
        const user = await authApi.getCurrentUserIfExists();
        if (cancelled) return;
        if (user) {
          setAuthUser(user.id, user.role as UserRole, user.isSelfManagingOwner ?? false);
        } else {
          logger.warn(
            '[ClerkAuthBridge] No UserProfile for this Clerk session (401 or empty). Demo/TestDB users need `clerkUserId` set — run `pnpm run stripe:push-demos` (or equivalent) so JWT `sub` matches Prisma.',
            { module: 'ClerkAuthBridge', action: 'get_current_user_if_exists' },
          );
        }
      } catch (err) {
        if (!cancelled) logger.error('[ClerkAuthBridge] getCurrentUserIfExists failed', err);
      } finally {
        if (!cancelled) setAuthPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, setAuthUser, setAuthPending]);

  const clerkSignOut = useMemo(
    () => async () => {
      await clerkSignOutFn?.();
      clearSessionAndRedirectToLogin();
    },
    [clerkSignOutFn]
  );

  return (
    <SignOutContext.Provider value={clerkSignOut}>
      {children}
    </SignOutContext.Provider>
  );
};
